import { ajaxPromise, capitalizeFirst, displayNotification, htmlEsc, logerror, logme } from './utils';
import { LOCK_TYPE_ASSESSMENT_ONBOARDING, LOCK_TYPE_ASSESSMENT_OFFBOARDING, LOCK_TYPE_ASSESSMENT_CONCHECKLIST, LOCK_TYPE_ASSESSMENT_CONTRACT } from './constants';
import { LOCK_TYPE_ASSESSMENT_DILIGENCE, LOCK_TYPE_ASSESSMENT_INHERENT, LOCK_TYPE_ASSESSMENT_REVIEW, LOCK_TYPE_ASSESSMENT_VENDORVALUE, LOCK_TYPE_CONTRACT_DETAIL, LOCK_TYPE_CONTRACT_SCORING } from './constants';
export interface LockConfig {
    lockType: number;
    recordId: number;
    lockHeartbeatMinutes: number;
    lockTimeoutMinutes: number;
    bannerLocation: JQuery<HTMLElement>;
    bannerRefreshSeconds?: number;
    options?: Record<string, any>;
    toggle?: (disabled: boolean) => void; // the function to disabled / enable the record
    onInactivitySave?: () => Promise<void>; // Save progress when inactivity timeout occurs (before lock release)
    onInactivityNavigation?: () => void; // Navigate away after the countdown modal (after lock release)
    onRefresh?: () => void; // Callback for when lock is acquired after a refresh (to get latest data)
    redirectText?: string; // Custom text describing where user will be redirected (e.g., "vendor details", "assessment list")
}
export class RecordLock {
    private lockType: number;
    private recordId: number;
    private hasLock = false;
    private lockHeartbeatMinutes: number;
    private bannerRefreshSeconds: number;
    private lockedByOther = false;
    private lockInterval: number | null = null;
    private bannerLocation: JQuery<HTMLElement>;
    private options?: Record<string, any>;
    private activityListeners: (() => void)[] = [];
    private lockTimeoutMinutes: number;
    private inactivityTimer: number | null = null;
    private toggle?: (disabled: boolean) => void;
    private onInactivitySave?: () => Promise<void>;
    private onInactivityNavigation?: () => void;
    private onRefresh?: () => void;
    private redirectText?: string;

    constructor(config: LockConfig) {
        this.lockType = config.lockType;
        this.recordId = config.recordId;
        this.lockTimeoutMinutes = config.lockTimeoutMinutes;
        this.lockHeartbeatMinutes = config.lockHeartbeatMinutes;
        this.bannerRefreshSeconds = config.bannerRefreshSeconds ?? 30;
        this.bannerLocation = config.bannerLocation;
        this.options = config?.options;
        this.redirectText = config.redirectText;
        this.toggle = config.toggle;
        this.onInactivitySave = config.onInactivitySave;
        this.onInactivityNavigation = config.onInactivityNavigation;
        this.onRefresh = config.onRefresh;
        this.setupPageHideHandler();
    }

    async startLock(): Promise<boolean> {
        try {
            const res = await ajaxPromise('/form/submit', {
                type: 'lock_start',
                data: {
                    lock_type: this.lockType,
                    record_id: this.recordId,
                    ...this.options,
                }
            });

            switch (res.rc) {
                case 'OK':
                    this.hasLock = true;
                    this.startHeartbeat();
                    this.setupActivityTracking();
                    this.toggle?.(false); // enable the record
                    return true;
                case 'LOCKED':
                    this.lockedByOther = true;
                    cleanupLockBanner();
                    this.bannerLocation?.prepend(createLockedBanner({
                        recordLock: this,
                        lockType: this.lockType,
                        lockedByUser: res.locked_by,
                        lockedAt: res.locked_at,
                        onRefresh: () => this.toggle?.(false),
                        refreshIntervalSeconds: this.bannerRefreshSeconds
                    }));
                    this.toggle?.(true); // disable the record
                    return false;
                case 'NO_OPTION':
                    displayNotification('Lock Error', 'Record locking is not enabled', 'warning');
                    return true;
                case 'BAD_ASSESSMENT_STATUS':
                    if ($('#record_locked_banner').length) {
                        cleanupLockBanner();
                        this.toggle?.(true);
                    }
                    return true;
            }
        } catch (error) {
            logerror('Failed to acquire lock', error);
            displayNotification('Lock Error', 'Failed to acquire lock', 'danger');
            return false;
        }
        return false;
    }

    private setupActivityTracking() {
        this.activityListeners.forEach(cleanup => cleanup());
        this.activityListeners = [];
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
        }

        const resetInactivityTimer = () => {
            if (this.inactivityTimer) {
                clearTimeout(this.inactivityTimer);
            }

            this.inactivityTimer = window.setTimeout(async () => {
                if (this.hasLock) {
                    logme(`Releasing lock due to ${this.lockTimeoutMinutes} minutes of inactivity`);

                    // Save progress before releasing the lock
                    // This ensures any save operations complete before other users can acquire the lock
                    if (this.onInactivitySave) {
                        await this.onInactivitySave();
                    }

                    await this.releaseLock();
                    this.showInactivityLockoutModal();
                }
            }, this.lockTimeoutMinutes * 60 * 1000);
        };

        const activityEvents = ['mousemove', 'keydown', 'scroll', 'click', 'focus', 'blur'];
        activityEvents.forEach(eventType => {
            const listener = () => resetInactivityTimer();
            document.addEventListener(eventType, listener, true);
            this.activityListeners.push(() => document.removeEventListener(eventType, listener, true));
        });

        resetInactivityTimer(); // Initialize timer
    }

    private startHeartbeat() {
        if (this.lockInterval) clearInterval(this.lockInterval);

        this.lockInterval = window.setInterval(async () => {
            if (!this.hasLock) return;

            try {
                const res = await ajaxPromise('/form/submit', {
                    type: 'lock_heartbeat',
                    data: {
                        lock_type: this.lockType,
                        record_id: this.recordId
                    }
                });

                if (res.rc === 'LOST') {
                    this.hasLock = false;
                    clearInterval(this.lockInterval!);
                    this.toggle?.(true);
                    displayNotification('Lock Lost', 'Editing has been disabled. Please refresh the page.', 'danger');
                } else if (res.rc === 'NO_OPTION') {
                    displayNotification('Lock Error', 'Record locking is not enabled', 'warning');
                    clearInterval(this.lockInterval!);
                }
            } catch (error) {
                logerror('Heartbeat failed', error);
                // Don't immediately release lock on network errors - inactivity timer will handle it if needed
            }
        }, this.lockHeartbeatMinutes * 60 * 1000);
    }

    async releaseLock(): Promise<void> {
        if (!this.hasLock) return;

        try {
            await ajaxPromise('/form/submit', {
                type: 'lock_release',
                data: {
                    lock_type: this.lockType,
                    record_id: this.recordId
                }
            });

            this.hasLock = false;
            this.cleanupListenersAndInterval();
        } catch (error) {
            logerror('Failed to release lock', error);
        }
    }

    private cleanupListenersAndInterval() {
        this.activityListeners.forEach(cleanup => cleanup());
        this.activityListeners = [];
        if (this.lockInterval) {
            clearInterval(this.lockInterval);
            this.lockInterval = null;
        }
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
            this.inactivityTimer = null;
        }
    }

    private showInactivityLockoutModal(callbackExecuted = false) {
        const recordType = getRecordTypeName(this.lockType);
        let countdown = 10;
        let countdownTimer: number | null = null;

        const updateModalContent = (seconds: number) => {
            let redirectDestination = this.redirectText;
            if (!redirectDestination) {
                switch (this.lockType) {
                    case LOCK_TYPE_CONTRACT_DETAIL:
                    case LOCK_TYPE_CONTRACT_SCORING:
                        redirectDestination = 'the vendor edit contract list';
                        break;
                    case LOCK_TYPE_ASSESSMENT_INHERENT:
                    case LOCK_TYPE_ASSESSMENT_DILIGENCE:
                    case LOCK_TYPE_ASSESSMENT_REVIEW:
                    case LOCK_TYPE_ASSESSMENT_VENDORVALUE:
                        redirectDestination = 'the vendor edit assessment list';
                        break;
                    case LOCK_TYPE_ASSESSMENT_ONBOARDING:
                    case LOCK_TYPE_ASSESSMENT_OFFBOARDING:
                        redirectDestination = 'vendor details';
                        break;
                    case LOCK_TYPE_ASSESSMENT_CONCHECKLIST:
                    case LOCK_TYPE_ASSESSMENT_CONTRACT:
                        redirectDestination = 'contract edit';
                        break;
                    default:
                        redirectDestination = 'the previous page';
                        break;
                }
            }

            $('#inactivity_lockout_body').html(`
                <p>Your ${recordType} editing session has expired due to ${this.lockTimeoutMinutes} minutes of inactivity.</p>
                <p>You will be redirected back to ${redirectDestination} in <strong>${seconds}</strong> seconds.</p>
            `);
        };

        const executeCallback = () => {
            if (countdownTimer) {
                clearInterval(countdownTimer);
                countdownTimer = null;
            }
            $('#inactivity_lockout_dialog').modal('hide');
            if (!callbackExecuted && this.onInactivityNavigation) {
                this.onInactivityNavigation();
            }
        };

        updateModalContent(countdown);
        $('#inactivity_lockout_action').off('click').on('click', executeCallback);

        countdownTimer = window.setInterval(() => {
            countdown--;
            if (countdown <= 0) { // once we have reached 0, execute the callback
                executeCallback();
            } else { // otherwise, update the modal content and button text
                updateModalContent(countdown);
                $('#inactivity_lockout_action').text(`Continue (${countdown})`);
            }
        }, 1000);

        $('#inactivity_lockout_dialog').modal({
            backdrop: 'static',
            keyboard: false
        });

        // Reset button text when modal is shown
        $('#inactivity_lockout_action').text('Continue (10)');
    }

    private setupPageHideHandler() {
        window.addEventListener('pagehide', async () => {
            if (!this.hasLock) return;

            const hasBeacon = navigator.sendBeacon;

            if (!hasBeacon) {
                await this.releaseLock();
                return;
            }

            // Clean up listeners before page unload
            this.cleanupListenersAndInterval();

            // Send beacon to release lock
            const lockData = new FormData();
            lockData.append('type', 'lock_release');
            lockData.append('json', 'true');
            lockData.append('data', JSON.stringify({
                lock_type: this.lockType,
                record_id: this.recordId
            }));
            navigator.sendBeacon('/form/submit', lockData);

            // Mark as no longer having lock
            this.hasLock = false;
        });
    }

    // Check if current user has the lock
    get isLockedByMe(): boolean {
        return this.hasLock;
    }

    // Check if someone else has the lock
    get isLockedByOther(): boolean {
        return this.lockedByOther;
    }

    // Check if record is locked by anyone
    get isLocked(): boolean {
        return this.hasLock || this.lockedByOther;
    }

    async callOnRefresh(): Promise<void> {
        if (this.onRefresh) {
            await this.onRefresh();
        }
    }
}

export const getRecordTypeName = (lockType: number): string => {
    switch (lockType) {
        case LOCK_TYPE_CONTRACT_DETAIL:
            return 'contract';
        case LOCK_TYPE_CONTRACT_SCORING:
            return 'contract (assessment)';
        case LOCK_TYPE_ASSESSMENT_INHERENT:
        case LOCK_TYPE_ASSESSMENT_DILIGENCE:
        case LOCK_TYPE_ASSESSMENT_REVIEW:
        case LOCK_TYPE_ASSESSMENT_VENDORVALUE:
        case LOCK_TYPE_ASSESSMENT_ONBOARDING:
        case LOCK_TYPE_ASSESSMENT_OFFBOARDING:
        case LOCK_TYPE_ASSESSMENT_CONCHECKLIST:
        case LOCK_TYPE_ASSESSMENT_CONTRACT:
            return 'assessment';
        default:
            return 'record';
    }
};

export const createLockedBanner = ({ lockType, lockedByUser, lockedAt, recordLock, onRefresh, refreshIntervalSeconds = 30 }: { lockType: number, lockedByUser: string, lockedAt: string, recordLock: RecordLock, onRefresh: () => void, refreshIntervalSeconds?: number }) => {
    const lockTypeText = getRecordTypeName(lockType);

    const banner = `
        <div id="record_locked_banner" class="alert alert-warning" style="margin: 0 0 20px 0; border-radius: 0;">
            <div class="row">
                <div class="col-sm-8">
                    <h4><i class="fa fa-lock"></i> ${capitalizeFirst(lockTypeText)} Currently Being Edited</h4>
                    <p><strong>${htmlEsc(lockedByUser)}</strong> is currently editing this ${lockTypeText}.</p>
                    <p><small>Started editing: ${lockedAt}</small></p>
                </div>
                <div class="col-sm-4 text-right">
                    <button id="refresh_lock_status" class="btn btn-default">
                        <i class="fa fa-refresh"></i> Refresh
                    </button>
                </div>
            </div>
        </div>
    `;

    const refresh = async (isManual: boolean = false) => {
        const $button = $('#refresh_lock_status');
        $button.prop('disabled', true);

        const acquired = await recordLock?.startLock();
        if (!acquired) {
            if (isManual) displayNotification(`${capitalizeFirst(lockTypeText)} in use`, `This ${lockTypeText} is currently being edited by another user. Please try again later.`, 'warning');
            $button.prop('disabled', false);
            return;
        }

        cleanupLockBanner();
        onRefresh();

        if (recordLock) {
            await recordLock.callOnRefresh();
        }

        $button.prop('disabled', false);
    };

    if (refresh) {
        setTimeout(() => {
            $('#refresh_lock_status').on('click', () => refresh(true));

            const refreshInterval = setInterval(() => refresh(), refreshIntervalSeconds * 1000); // call refresh function at configurable interval

            $('#record_locked_banner').data('refreshInterval', refreshInterval); // Store interval ID so it can be cleared when banner is removed
        }, 100);
    }

    return banner;
};

export const clearLockBannerTimer = () => {
    const $banner = $('#record_locked_banner');
    const refreshInterval = $banner.data('refreshInterval');
    if (refreshInterval) {
        clearInterval(refreshInterval);
        $banner.removeData('refreshInterval');
    }
};

export const cleanupLockBanner = () => {
    clearLockBannerTimer();
    $('#record_locked_banner').remove();
};

export const toggleAssessmentEditing = ($container: $, isDisabled: boolean = false, cleanupBanner: boolean = false) => {
    $container.find('#response_form input, #response_form select, #response_form textarea, #response_form_reviewedby, #response_form_date').prop('disabled', isDisabled).trigger('chosen:updated');
    $container.find('#response_form_submit').prop('disabled', isDisabled);
    $container.find('#response_form_lock').prop('disabled', isDisabled);
    $container.find('#response_form_reviewers').prop('disabled', isDisabled).trigger('chosen:updated');;
    $container.find('#response_form_review').prop('disabled', isDisabled)
    $container.find('#response_form_delete').prop('disabled', isDisabled);
    $container.find('[data-target="#task_add_modal"]').prop('disabled', isDisabled);
    $container.find('#response_form_task_submit_external').prop('disabled', isDisabled);

    const $editBtn = $container.find('#response_form_unlock');
    isDisabled ? $editBtn.hide() : $editBtn.show()

    if (cleanupBanner) {
        cleanupLockBanner();
    }
};

export const startLockChecking = (lockCheckIntervalRef, intervalFunc, heartBeat) => {
    if (lockCheckIntervalRef) clearInterval(lockCheckIntervalRef);
    lockCheckIntervalRef = setInterval(intervalFunc, heartBeat);
};

export const stopLockChecking = (lockCheckIntervalRef) => {
    if (lockCheckIntervalRef) {
        clearInterval(lockCheckIntervalRef);
        lockCheckIntervalRef = null;
    }
};